// Tile catalogue: per-tile, per-face data (disconnected nodes + resources) for
// the double-sided terrain tiles the board is assembled from. Pure data.
//
// Tile-local node indices are small integers `0..nodeCount-1`; board assembly
// maps them onto global graph node ids when a tile is placed and oriented.
function res(node, type, permanence = "permanent") {
    return { node, type, permanence };
}
// --- Summit ---------------------------------------------------------------
/**
 * SUMMIT tiles — the highest altitude ("The Red Core").
 *
 * 3 double-sided tiles, 8 node positions each (24 nodes total across the zone).
 * Each tile is one red diamond; its two faces (A / B) reuse the same 8 node
 * positions with different resource/connection configs. The source pictures
 * show both faces of a tile stacked together (top diamond = Face A, bottom
 * diamond = Face B).
 *
 * Node layout: the "diamond" is only the printed OUTLINE of the tile. The nodes
 * are NOT arranged in a diamond — they're corners of the board-wide honeycomb
 * lattice (every node has ≤3 edges meeting at ~120°; see CLAUDE.md), the same
 * lattice that underlies every tile shape (triangles, trapezoids, …). A tile's
 * `nodeCount` is just however many lattice corners the outline encloses; only
 * the silhouette changes between zones, never the corner pattern. The 8 local
 * indices are therefore a stable reading-order handle for the data, NOT a
 * literal geometry — top, then down the diamond, then bottom:
 *
 *   top → upper pair → mid pair → lower pair → bottom
 *    0       1   2       3   4       5   6        7
 *
 * (A previous version drew 1-3-5 / 2-4-6 as straight vertical columns joined
 * into an 8-node ring. That's impossible on a honeycomb: edges never run
 * collinear, and the smallest cycles are 6- then 10-loops — never 8. Exact
 * corner positions get pinned at board assembly, when these local indices map
 * onto global graph node ids.)
 *
 * NOTE: resource *types* are confident (blue hex = minerals, leaf = food,
 * flower / atom-star = vigor, grey diamond = obsidian). Exact node indices and
 * permanent-vs-one-time (read as solid = permanent, faded = one-time) are a
 * best-effort read of the pictures and may need touch-up.
 *
 * The `disconnectedPaths` (severed edges — the faint dotted chasms) below are a
 * GUESS from the photos; verify/correct each face on `/library/tiles`. Severing
 * is per-PATH, so a node keeps its other edges. Local layout + edges:
 *     0  1        (top edge)
 *   2 3  4 5      (middle row; 2 = left point, 5 = right point)
 *     6  7        (bottom edge)
 *   edges: 0-1, 0-3, 1-4, 2-3, 3-6, 4-5, 4-7, 6-7
 */
export const SUMMIT_NODE_COUNT = 8;
// Re-read from the photos (each picture = one tile; top diamond = Face A,
// bottom diamond = Face B). Mapped to the slot's actual layout above. Still a
// best-effort visual read — `disconnectedPaths` (the dotted chasms) couldn't be
// read confidently and are left empty; set them on /library/tiles.
export const SUMMIT_TILES = [
    {
        id: "summit-1",
        zone: "summit",
        nodeCount: SUMMIT_NODE_COUNT,
        faceA: {
            disconnectedPaths: [[3, 6]],
            disconnectedExits: [[1, 1]],
            resources: [
                res(1, "food", "oneTime"),
                res(3, "food", "permanent"),
                res(4, "minerals", "permanent"),
            ],
        },
        faceB: {
            disconnectedPaths: [
                [0, 3],
                [6, 7],
            ],
            disconnectedExits: [[7, 5]],
            resources: [
                res(0, "obsidian", "permanent"),
                res(6, "obsidian", "oneTime"),
                res(7, "minerals", "permanent"),
            ],
        },
    },
    {
        id: "summit-2",
        zone: "summit",
        nodeCount: SUMMIT_NODE_COUNT,
        // Pic 2 top: dark hex (obsidian), blue hex (mineral, faded), faded dark token.
        faceA: {
            disconnectedPaths: [
                [0, 1],
                [3, 6],
                [4, 7],
            ],
            resources: [
                res(0, "vigor", "permanent"),
                res(2, "minerals", "oneTime"),
                res(7, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [],
            disconnectedExits: [
                [5, 1],
                [5, 5],
                [7, 5],
            ],
            resources: [
                res(1, "food", "permanent"),
                res(3, "minerals", "permanent"),
                res(7, "food", "oneTime"),
            ],
        },
    },
    {
        id: "summit-3",
        zone: "summit",
        nodeCount: SUMMIT_NODE_COUNT,
        // Pic 3 top: purple teardrop (vigor, faded), blue hex (mineral), vigor.
        faceA: {
            disconnectedPaths: [
                [1, 4],
                [3, 6],
            ],
            disconnectedExits: [[2, 2]],
            resources: [
                res(0, "obsidian", "permanent"),
                res(5, "obsidian", "oneTime"),
                res(7, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [
                [4, 7],
                [6, 7],
            ],
            disconnectedExits: [[0, 2]],
            resources: [
                res(1, "minerals", "permanent"),
                res(2, "vigor", "oneTime"),
                res(5, "vigor", "permanent"),
            ],
        },
    },
];
// NOTE (2026-07-10, machine extraction from "Fortress Board Pieces.pdf"):
// the verified summit data matches the PDF exactly on 4 of 6 faces. Three
// small diffs on the other two (kept as the user verified them; see the PDF
// if they need a second look):
//   - summit-1 faceA res(4, "minerals"): the PDF token is one-time (outlined).
//   - summit-1 faceA: the PDF also severs exit [0, 2].
//   - summit-2 faceB exits: the PDF severs [6, 4], not [7, 5].
// --- Crag -----------------------------------------------------------------
/**
 * CRAG tiles — the 2nd-highest altitude (yellow trapezoids, "−3 ✱ +2": Vigor
 * cost 3, resource bonus +2). 6 double-sided tiles, 21 node positions each.
 *
 * Local node layout — the slot's canonical node order (`makeSlot`: outermost
 * row first, then left → right). Six short rows tapering from the wide
 * ridgeline-facing edge (5 nodes) to the narrow summit-facing tip (2):
 *
 *   row 0 (wide, toward ridgeline):  0  1  2  3  4
 *   row 1:                            5  6  7  8
 *   row 2:                            9 10 11 12
 *   row 3:                           13 14 15
 *   row 4:                           16 17 18
 *   row 5 (narrow, toward summit):   19 20
 *
 * There are NO within-row edges; the real internal edges (the only valid
 * `disconnectedPaths` pairs) are:
 *   0-5 1-5 1-6 2-6 2-7 3-7 3-8 4-8    (rows 0↔1)
 *   5-9 6-10 7-11 8-12                 (rows 1↔2)
 *   9-13 10-13 10-14 11-14 11-15 12-15 (rows 2↔3)
 *   13-16 14-17 15-18                  (rows 3↔4)
 *   16-19 17-19 17-20 18-20            (rows 4↔5)
 *
 * MACHINE-EXTRACTED (2026-07-10) from the vector art in
 * "Fortress Board Pieces.pdf" (crag = the yellow trapezoids on pages 1-4;
 * 12 faces). Resource type from token fill colour (blue hex = minerals, green
 * crescent = food, pink teardrop = vigor, dark elongated hex = obsidian);
 * one-time = the outlined gradient token variant; severed paths/exits = edges
 * left dash-only (connected edges are overdrawn solid). The pipeline
 * reproduced the hand-verified summit faces, so the read is trusted. Faces
 * are paired into tiles by PDF reading order — the physical A/B pairing is
 * NOT recoverable from the PDF, so the pairing (not the face data) is a
 * convention; each face comment cites its source page/position.
 */
export const CRAG_NODE_COUNT = 21;
export const CRAG_TILES = [
    {
        id: "crag-1",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p1 face @(353,307) / p1 @(47,571)
        faceA: {
            disconnectedPaths: [[1, 5], [2, 7], [11, 15], [17, 19]],
            disconnectedExits: [[2, 2], [12, 0], [19, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(1, "minerals"),
                res(4, "minerals"),
                res(11, "obsidian", "oneTime"),
                res(12, "minerals", "oneTime"),
                res(15, "minerals"),
                res(19, "obsidian"),
                res(20, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 6], [7, 11], [14, 17]],
            disconnectedExits: [[0, 3], [3, 2], [4, 5], [19, 5]],
            resources: [
                res(1, "vigor", "oneTime"),
                res(3, "vigor"),
                res(4, "minerals", "oneTime"),
                res(5, "vigor"),
                res(11, "vigor"),
                res(12, "minerals", "oneTime"),
                res(16, "minerals", "oneTime"),
                res(19, "vigor"),
            ],
        },
    },
    {
        id: "crag-2",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p1 face @(262,571) / p2 @(353,307)
        faceA: {
            disconnectedPaths: [[4, 8], [11, 15], [17, 19]],
            disconnectedExits: [[1, 2], [2, 2], [9, 3], [20, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(6, "food"),
                res(8, "minerals", "oneTime"),
                res(9, "minerals", "oneTime"),
                res(15, "food"),
                res(16, "food"),
                res(17, "food"),
                res(19, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 8], [9, 13], [14, 17], [17, 20]],
            disconnectedExits: [[0, 2], [2, 2], [9, 3]],
            resources: [
                res(2, "obsidian", "oneTime"),
                res(8, "minerals"),
                res(9, "obsidian"),
                res(12, "minerals", "oneTime"),
                res(13, "minerals", "oneTime"),
                res(17, "obsidian"),
            ],
        },
    },
    {
        id: "crag-3",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p2 face @(47,571) / p2 @(262,571)
        faceA: {
            disconnectedPaths: [[10, 13], [11, 14], [13, 16], [15, 18]],
            disconnectedExits: [[2, 2], [4, 2], [20, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(3, "vigor", "oneTime"),
                res(7, "food"),
                res(13, "vigor"),
                res(18, "obsidian"),
                res(19, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 7], [8, 12], [17, 20]],
            disconnectedExits: [[2, 2], [3, 2], [9, 3], [16, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(5, "minerals"),
                res(8, "vigor"),
                res(9, "minerals", "oneTime"),
                res(11, "vigor", "oneTime"),
                res(16, "food"),
                res(19, "minerals", "oneTime"),
                res(20, "minerals"),
            ],
        },
    },
    {
        id: "crag-4",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p3 face @(353,307) / p3 @(47,571)
        faceA: {
            disconnectedPaths: [[5, 9], [6, 10], [11, 14], [16, 19]],
            disconnectedExits: [[9, 3], [12, 0], [20, 5]],
            resources: [
                res(2, "minerals"),
                res(4, "obsidian", "oneTime"),
                res(5, "minerals", "oneTime"),
                res(8, "minerals", "oneTime"),
                res(10, "obsidian"),
                res(12, "minerals"),
                res(14, "minerals", "oneTime"),
                res(19, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[4, 8], [6, 10], [15, 18]],
            disconnectedExits: [[0, 2], [1, 2], [12, 0], [16, 3]],
            resources: [
                res(1, "vigor"),
                res(2, "vigor", "oneTime"),
                res(8, "vigor"),
                res(9, "vigor"),
                res(11, "vigor", "oneTime"),
                res(13, "minerals", "oneTime"),
                res(15, "vigor"),
                res(20, "vigor", "oneTime"),
            ],
        },
    },
    {
        id: "crag-5",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p3 face @(262,571) / p4 @(353,307)
        faceA: {
            disconnectedPaths: [[2, 7], [3, 7], [7, 11], [14, 17]],
            disconnectedExits: [[4, 2], [9, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(8, "food"),
                res(10, "minerals", "oneTime"),
                res(13, "food"),
                res(14, "food"),
                res(18, "food", "oneTime"),
                res(19, "food"),
                res(20, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 5], [6, 10], [8, 12], [11, 15], [16, 19]],
            disconnectedExits: [[4, 5], [16, 3]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(3, "minerals", "oneTime"),
                res(4, "minerals", "oneTime"),
                res(6, "obsidian"),
                res(12, "obsidian"),
                res(14, "obsidian", "oneTime"),
                res(16, "minerals"),
                res(17, "minerals"),
            ],
        },
    },
    {
        id: "crag-6",
        zone: "crag",
        nodeCount: CRAG_NODE_COUNT,
        // PDF p4 face @(47,571) / p4 @(262,571)
        faceA: {
            disconnectedPaths: [[1, 6], [7, 11], [10, 13], [12, 15], [17, 19]],
            disconnectedExits: [[1, 2], [18, 0]],
            resources: [
                res(5, "minerals", "oneTime"),
                res(6, "vigor"),
                res(8, "food"),
                res(12, "vigor", "oneTime"),
                res(13, "food", "oneTime"),
                res(19, "obsidian"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 6], [11, 15], [17, 19]],
            disconnectedExits: [[2, 2], [3, 2], [9, 3], [16, 3]],
            resources: [
                res(1, "minerals"),
                res(7, "vigor"),
                res(8, "vigor", "oneTime"),
                res(9, "vigor", "oneTime"),
                res(13, "food", "oneTime"),
                res(15, "food"),
                res(19, "minerals"),
                res(20, "food", "oneTime"),
            ],
        },
    },
];
// --- Ridgeline ------------------------------------------------------------
/**
 * RIDGELINE tiles — the 3rd-highest altitude (orange triangles, "−1 ✱ +1":
 * Vigor cost 1, resource bonus +1). 18 double-sided tiles, 25 nodes each.
 *
 * Local node layout — apex-at-top reading order (canonical slot order):
 *
 *   row 0 (apex):                 0
 *   row 1:                      1  2  3
 *   row 2:                   4  5  6  7  8
 *   row 3:                9 10 11 12 13 14 15
 *   row 4 (base):     16 17 18 19 20 21 22 23 24
 *
 * MACHINE-EXTRACTED (2026-07-10) from "Fortress Board Pieces.pdf" (ridgeline =
 * the orange triangles on pages 1-9, 4 per page = 36 faces). Same encoding and
 * pairing convention as the crag section above.
 */
export const RIDGELINE_NODE_COUNT = 25;
export const RIDGELINE_TILES = [
    {
        id: "ridgeline-1",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p1 face @(48,44) / p1 @(201,44)
        faceA: {
            disconnectedPaths: [[0, 2], [4, 8], [8, 10], [11, 15], [17, 18], [21, 23]],
            disconnectedExits: [[3, 1], [9, 1], [15, 5]],
            resources: [
                res(0, "food", "oneTime"),
                res(1, "minerals"),
                res(6, "minerals", "oneTime"),
                res(7, "minerals", "oneTime"),
                res(10, "minerals", "oneTime"),
                res(11, "vigor"),
                res(13, "minerals"),
                res(17, "vigor", "oneTime"),
                res(19, "minerals"),
                res(23, "food"),
                res(24, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [4, 8], [6, 11], [11, 15], [15, 16], [17, 18], [21, 23]],
            disconnectedExits: [[0, 1], [24, 5]],
            resources: [
                res(0, "minerals"),
                res(2, "minerals"),
                res(6, "obsidian", "oneTime"),
                res(8, "minerals"),
                res(11, "obsidian"),
                res(12, "minerals", "oneTime"),
                res(14, "minerals", "oneTime"),
                res(16, "minerals", "oneTime"),
                res(18, "obsidian", "oneTime"),
                res(22, "minerals"),
            ],
        },
    },
    {
        id: "ridgeline-2",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p1 face @(48,307) / p1 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 4], [6, 11], [8, 13], [10, 14], [12, 16], [19, 20]],
            disconnectedExits: [[0, 3], [9, 5], [21, 3]],
            resources: [
                res(0, "vigor", "oneTime"),
                res(1, "vigor", "oneTime"),
                res(7, "vigor", "oneTime"),
                res(9, "vigor"),
                res(10, "vigor"),
                res(11, "vigor"),
                res(12, "minerals"),
                res(15, "food", "oneTime"),
                res(20, "food"),
                res(23, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [3, 4], [6, 11], [12, 13], [17, 20], [21, 23]],
            disconnectedExits: [[9, 1], [14, 3], [15, 5]],
            resources: [
                res(2, "food", "oneTime"),
                res(3, "minerals"),
                res(6, "food"),
                res(8, "food", "oneTime"),
                res(9, "vigor"),
                res(10, "food"),
                res(16, "food", "oneTime"),
                res(20, "food"),
                res(23, "vigor", "oneTime"),
                res(24, "food", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-3",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p2 face @(49,44) / p2 @(201,44)
        faceA: {
            disconnectedPaths: [[2, 5], [7, 12], [8, 13], [13, 17], [22, 23]],
            disconnectedExits: [[0, 1], [3, 1], [9, 1], [19, 5]],
            resources: [
                res(4, "food"),
                res(8, "minerals"),
                res(9, "food"),
                res(11, "food", "oneTime"),
                res(15, "minerals", "oneTime"),
                res(16, "food", "oneTime"),
                res(17, "food"),
                res(21, "food", "oneTime"),
                res(23, "food"),
                res(24, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [3, 7], [6, 7], [8, 10], [13, 17], [19, 20]],
            disconnectedExits: [[0, 1], [15, 5], [19, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(1, "vigor", "oneTime"),
                res(2, "minerals"),
                res(4, "vigor", "oneTime"),
                res(8, "vigor", "oneTime"),
                res(12, "vigor"),
                res(16, "vigor"),
                res(18, "vigor"),
                res(22, "food", "oneTime"),
                res(24, "vigor"),
            ],
        },
    },
    {
        id: "ridgeline-4",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p2 face @(49,307) / p2 @(201,307)
        faceA: {
            disconnectedPaths: [[4, 8], [6, 7], [10, 14], [12, 13], [19, 20]],
            disconnectedExits: [[1, 1], [5, 3], [9, 5], [19, 5]],
            resources: [
                res(2, "minerals", "oneTime"),
                res(3, "obsidian", "oneTime"),
                res(4, "vigor", "oneTime"),
                res(8, "obsidian"),
                res(11, "minerals"),
                res(12, "minerals", "oneTime"),
                res(18, "vigor", "oneTime"),
                res(19, "vigor"),
                res(22, "vigor"),
            ],
        },
        faceB: {
            disconnectedPaths: [[4, 8], [9, 11], [11, 15], [13, 17], [16, 19], [23, 24]],
            disconnectedExits: [[0, 1], [14, 3], [21, 3]],
            resources: [
                res(1, "minerals", "oneTime"),
                res(2, "minerals", "oneTime"),
                res(6, "food", "oneTime"),
                res(8, "minerals"),
                res(9, "obsidian"),
                res(13, "food", "oneTime"),
                res(16, "food"),
                res(21, "food"),
                res(22, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-5",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p3 face @(49,44) / p3 @(201,44)
        faceA: {
            disconnectedPaths: [[3, 7], [9, 11], [14, 18], [15, 16], [18, 21], [22, 23]],
            disconnectedExits: [[1, 1], [24, 5]],
            resources: [
                res(4, "minerals"),
                res(9, "obsidian", "oneTime"),
                res(11, "minerals"),
                res(12, "obsidian", "oneTime"),
                res(18, "obsidian"),
                res(21, "minerals", "oneTime"),
                res(23, "minerals", "oneTime"),
                res(24, "obsidian"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [3, 7], [14, 18], [15, 16], [18, 21], [20, 22], [22, 23]],
            disconnectedExits: [[6, 1], [9, 1]],
            resources: [
                res(2, "minerals", "oneTime"),
                res(4, "minerals"),
                res(6, "obsidian", "oneTime"),
                res(12, "obsidian", "oneTime"),
                res(15, "obsidian"),
                res(18, "obsidian"),
                res(21, "obsidian", "oneTime"),
                res(22, "obsidian"),
            ],
        },
    },
    {
        id: "ridgeline-6",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p3 face @(49,308) / p3 @(201,307)
        faceA: {
            disconnectedPaths: [[2, 5], [6, 11], [8, 10], [11, 15], [16, 19], [17, 20]],
            disconnectedExits: [[5, 3], [19, 5], [22, 5]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(7, "vigor"),
                res(9, "vigor", "oneTime"),
                res(10, "obsidian"),
                res(11, "obsidian"),
                res(20, "obsidian"),
                res(23, "obsidian", "oneTime"),
                res(24, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[6, 11], [7, 12], [8, 10], [11, 15], [12, 16], [18, 21]],
            disconnectedExits: [[0, 1], [22, 5], [24, 5]],
            resources: [
                res(2, "obsidian", "oneTime"),
                res(4, "food"),
                res(5, "obsidian", "oneTime"),
                res(7, "food", "oneTime"),
                res(11, "obsidian"),
                res(12, "obsidian"),
                res(19, "obsidian", "oneTime"),
                res(21, "obsidian"),
            ],
        },
    },
    {
        id: "ridgeline-7",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p4 face @(49,44) / p4 @(201,44)
        faceA: {
            disconnectedPaths: [[9, 11], [12, 13], [17, 18], [21, 23]],
            disconnectedExits: [[0, 1], [3, 1], [21, 3], [22, 5]],
            resources: [
                res(2, "vigor", "oneTime"),
                res(4, "food"),
                res(5, "vigor", "oneTime"),
                res(9, "vigor"),
                res(11, "minerals", "oneTime"),
                res(12, "food"),
                res(15, "food", "oneTime"),
                res(18, "vigor"),
                res(22, "vigor"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [3, 4], [6, 11], [8, 13]],
            disconnectedExits: [[0, 3], [14, 3], [15, 5], [22, 5], [24, 3]],
            resources: [
                res(2, "food"),
                res(3, "vigor"),
                res(5, "food", "oneTime"),
                res(8, "food"),
                res(11, "vigor", "oneTime"),
                res(12, "minerals", "oneTime"),
                res(13, "vigor"),
                res(14, "food", "oneTime"),
                res(20, "food"),
                res(24, "food", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-8",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p4 face @(49,307) / p4 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 4], [9, 11], [15, 16], [16, 19], [18, 21]],
            disconnectedExits: [[0, 1], [1, 1], [24, 3]],
            resources: [
                res(1, "food", "oneTime"),
                res(2, "minerals"),
                res(4, "minerals"),
                res(6, "minerals"),
                res(9, "food", "oneTime"),
                res(11, "minerals"),
                res(12, "minerals", "oneTime"),
                res(14, "food", "oneTime"),
                res(19, "minerals"),
                res(20, "minerals", "oneTime"),
                res(23, "food"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [3, 4], [10, 14], [11, 15], [12, 13], [22, 23]],
            disconnectedExits: [[0, 3], [6, 1], [19, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(1, "minerals"),
                res(5, "minerals", "oneTime"),
                res(7, "food"),
                res(9, "food", "oneTime"),
                res(11, "food", "oneTime"),
                res(14, "vigor"),
                res(16, "food"),
                res(18, "vigor", "oneTime"),
                res(21, "vigor", "oneTime"),
                res(23, "vigor"),
            ],
        },
    },
    {
        id: "ridgeline-9",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p5 face @(48,44) / p5 @(201,44)
        faceA: {
            disconnectedPaths: [[11, 15], [12, 16], [14, 18], [17, 20], [19, 20]],
            disconnectedExits: [[0, 3], [1, 1], [21, 3], [24, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(1, "minerals"),
                res(3, "vigor", "oneTime"),
                res(6, "minerals"),
                res(9, "vigor", "oneTime"),
                res(11, "minerals"),
                res(13, "vigor", "oneTime"),
                res(18, "minerals"),
                res(19, "minerals"),
                res(20, "vigor"),
                res(23, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [5, 10], [6, 11], [9, 11], [12, 16], [13, 17], [16, 19], [17, 18], [21, 23], [22, 23]],
            disconnectedExits: [[22, 5]],
            resources: [
                res(1, "vigor", "oneTime"),
                res(2, "food"),
                res(5, "obsidian", "oneTime"),
                res(8, "food", "oneTime"),
                res(11, "obsidian"),
                res(13, "obsidian", "oneTime"),
                res(18, "vigor"),
                res(22, "obsidian"),
            ],
        },
    },
    {
        id: "ridgeline-10",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p5 face @(48,307) / p5 @(201,307)
        faceA: {
            disconnectedPaths: [[4, 8], [8, 10], [9, 11], [12, 13], [17, 18], [19, 20]],
            disconnectedExits: [[0, 1], [6, 1], [19, 5]],
            resources: [
                res(2, "minerals"),
                res(4, "obsidian", "oneTime"),
                res(6, "minerals", "oneTime"),
                res(8, "obsidian", "oneTime"),
                res(11, "minerals"),
                res(12, "minerals"),
                res(14, "minerals"),
                res(20, "obsidian"),
                res(24, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 7], [4, 8], [6, 11], [10, 14], [20, 22]],
            disconnectedExits: [[9, 1], [22, 5], [24, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(2, "minerals", "oneTime"),
                res(4, "minerals"),
                res(5, "vigor", "oneTime"),
                res(6, "food", "oneTime"),
                res(7, "vigor"),
                res(11, "food"),
                res(16, "minerals"),
                res(17, "minerals", "oneTime"),
                res(18, "vigor", "oneTime"),
                res(22, "minerals"),
            ],
        },
    },
    {
        id: "ridgeline-11",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p6 face @(49,44) / p6 @(201,44)
        faceA: {
            disconnectedPaths: [[1, 4], [12, 13], [16, 19], [18, 21]],
            disconnectedExits: [[3, 1], [9, 1], [14, 3], [19, 5], [24, 5]],
            resources: [
                res(1, "minerals"),
                res(2, "vigor", "oneTime"),
                res(7, "food"),
                res(8, "food"),
                res(11, "food", "oneTime"),
                res(16, "vigor", "oneTime"),
                res(18, "vigor", "oneTime"),
                res(19, "vigor"),
                res(21, "food"),
                res(22, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [4, 8], [6, 11], [7, 12], [10, 14], [18, 21]],
            disconnectedExits: [[5, 3], [19, 5], [24, 3]],
            resources: [
                res(0, "vigor", "oneTime"),
                res(2, "food"),
                res(4, "food", "oneTime"),
                res(7, "vigor"),
                res(8, "minerals"),
                res(11, "food", "oneTime"),
                res(17, "food", "oneTime"),
                res(18, "food", "oneTime"),
                res(19, "vigor"),
                res(21, "vigor"),
            ],
        },
    },
    {
        id: "ridgeline-12",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p6 face @(49,307) / p6 @(201,307)
        faceA: {
            disconnectedPaths: [[2, 5], [6, 11], [7, 12], [11, 15], [17, 20]],
            disconnectedExits: [[1, 1], [14, 3], [24, 3], [24, 5]],
            resources: [
                res(0, "vigor"),
                res(4, "food", "oneTime"),
                res(7, "minerals"),
                res(8, "food", "oneTime"),
                res(10, "vigor"),
                res(11, "vigor"),
                res(15, "minerals", "oneTime"),
                res(19, "food", "oneTime"),
                res(20, "vigor"),
                res(22, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[4, 8], [8, 13], [12, 16], [20, 22], [21, 23]],
            disconnectedExits: [[0, 1], [6, 1], [14, 3], [24, 5]],
            resources: [
                res(1, "vigor", "oneTime"),
                res(4, "food"),
                res(5, "minerals", "oneTime"),
                res(9, "vigor", "oneTime"),
                res(11, "vigor", "oneTime"),
                res(12, "minerals"),
                res(15, "food"),
                res(21, "food"),
                res(22, "food"),
                res(23, "vigor", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-13",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p7 face @(49,44) / p7 @(201,44)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 7], [8, 13], [17, 20], [21, 23], [23, 24]],
            disconnectedExits: [[5, 3], [6, 1], [9, 1]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(3, "food", "oneTime"),
                res(4, "minerals"),
                res(11, "minerals", "oneTime"),
                res(17, "food"),
                res(19, "food"),
                res(20, "obsidian", "oneTime"),
                res(23, "obsidian"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [2, 5], [3, 7], [5, 10], [8, 10], [16, 19], [21, 23]],
            disconnectedExits: [[6, 1], [14, 3], [22, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(2, "obsidian"),
                res(6, "vigor"),
                res(8, "minerals"),
                res(9, "obsidian", "oneTime"),
                res(12, "vigor", "oneTime"),
                res(13, "obsidian", "oneTime"),
                res(22, "vigor"),
            ],
        },
    },
    {
        id: "ridgeline-14",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p7 face @(49,307) / p7 @(201,307)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 7], [6, 11], [16, 19], [19, 20], [22, 23]],
            disconnectedExits: [[0, 1], [1, 1], [21, 3]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(6, "obsidian", "oneTime"),
                res(7, "minerals"),
                res(9, "obsidian", "oneTime"),
                res(10, "obsidian"),
                res(19, "obsidian"),
                res(22, "minerals", "oneTime"),
                res(23, "obsidian"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [3, 4], [4, 8], [11, 15], [20, 22], [22, 23]],
            disconnectedExits: [[6, 1], [14, 3], [21, 3]],
            resources: [
                res(2, "obsidian"),
                res(7, "minerals"),
                res(9, "minerals", "oneTime"),
                res(11, "minerals", "oneTime"),
                res(13, "minerals"),
                res(14, "minerals", "oneTime"),
                res(15, "obsidian", "oneTime"),
                res(22, "obsidian"),
                res(24, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-15",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p8 face @(49,44) / p8 @(201,44)
        faceA: {
            disconnectedPaths: [[4, 8], [6, 7], [7, 12], [16, 19], [17, 18], [20, 22], [22, 23]],
            disconnectedExits: [[14, 3], [21, 3]],
            resources: [
                res(2, "food"),
                res(4, "obsidian", "oneTime"),
                res(7, "obsidian"),
                res(8, "obsidian", "oneTime"),
                res(9, "obsidian", "oneTime"),
                res(13, "food", "oneTime"),
                res(19, "obsidian"),
                res(23, "obsidian"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [3, 4], [5, 10], [7, 12], [8, 13], [9, 11], [12, 13], [22, 23]],
            disconnectedExits: [[22, 5]],
            resources: [
                res(0, "obsidian"),
                res(7, "vigor"),
                res(10, "obsidian"),
                res(11, "obsidian", "oneTime"),
                res(14, "vigor", "oneTime"),
                res(19, "obsidian", "oneTime"),
                res(22, "obsidian"),
                res(24, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-16",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p8 face @(49,307) / p8 @(201,307)
        faceA: {
            disconnectedPaths: [[0, 2], [1, 4], [9, 11], [12, 13]],
            disconnectedExits: [[0, 1], [9, 1], [15, 5], [19, 5], [21, 3]],
            resources: [
                res(1, "food"),
                res(6, "food", "oneTime"),
                res(7, "food"),
                res(8, "vigor"),
                res(9, "minerals", "oneTime"),
                res(13, "food", "oneTime"),
                res(15, "food"),
                res(17, "minerals", "oneTime"),
                res(23, "vigor"),
                res(24, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[5, 10], [6, 7], [10, 14], [16, 19], [17, 18]],
            disconnectedExits: [[3, 1], [9, 1], [22, 5], [24, 5]],
            resources: [
                res(1, "vigor", "oneTime"),
                res(2, "vigor", "oneTime"),
                res(6, "vigor"),
                res(8, "food"),
                res(12, "minerals", "oneTime"),
                res(13, "vigor", "oneTime"),
                res(15, "food"),
                res(17, "minerals", "oneTime"),
                res(19, "vigor"),
                res(21, "vigor"),
            ],
        },
    },
    {
        id: "ridgeline-17",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p9 face @(49,44) / p9 @(201,44)
        faceA: {
            disconnectedPaths: [[4, 8], [5, 10], [11, 15], [13, 17], [14, 18], [21, 23]],
            disconnectedExits: [[0, 1], [9, 5], [24, 5]],
            resources: [
                res(1, "minerals", "oneTime"),
                res(4, "minerals"),
                res(6, "minerals", "oneTime"),
                res(9, "vigor"),
                res(13, "vigor", "oneTime"),
                res(14, "food"),
                res(15, "food"),
                res(19, "minerals", "oneTime"),
                res(23, "vigor"),
                res(24, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[4, 8], [13, 17], [14, 18], [21, 23], [23, 24]],
            disconnectedExits: [[0, 1], [6, 1], [9, 5], [19, 5]],
            resources: [
                res(0, "food"),
                res(4, "minerals"),
                res(5, "minerals", "oneTime"),
                res(7, "minerals"),
                res(8, "food", "oneTime"),
                res(9, "minerals"),
                res(10, "minerals", "oneTime"),
                res(16, "minerals", "oneTime"),
                res(17, "minerals"),
                res(18, "minerals"),
                res(22, "food", "oneTime"),
            ],
        },
    },
    {
        id: "ridgeline-18",
        zone: "ridgeline",
        nodeCount: RIDGELINE_NODE_COUNT,
        // PDF p9 face @(49,307) / p9 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 2], [2, 5], [8, 13], [15, 16], [17, 18], [21, 23]],
            disconnectedExits: [[3, 1], [6, 1], [9, 5], [14, 3]],
            resources: [
                res(2, "obsidian"),
                res(6, "food"),
                res(8, "obsidian", "oneTime"),
                res(11, "minerals", "oneTime"),
                res(12, "obsidian", "oneTime"),
                res(20, "vigor"),
                res(21, "obsidian"),
                res(23, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [7, 12], [8, 13], [9, 11], [14, 18], [22, 23]],
            disconnectedExits: [[0, 1], [6, 1], [21, 3]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(2, "minerals"),
                res(3, "vigor", "oneTime"),
                res(5, "minerals", "oneTime"),
                res(7, "minerals"),
                res(11, "minerals"),
                res(12, "minerals", "oneTime"),
                res(13, "vigor"),
                res(18, "vigor", "oneTime"),
                res(19, "minerals"),
                res(23, "minerals"),
            ],
        },
    },
];
// --- Foothill ---------------------------------------------------------------
/**
 * FOOTHILL tiles — the 4th altitude (the white/cream triangles; no vigor cost
 * or bonus). 30 double-sided tiles, 25 nodes each — the same side-5 triangle
 * and apex-at-top local layout as ridgeline above.
 *
 * MACHINE-EXTRACTED (2026-07-10) from "Fortress Board Pieces.pdf" (foothill =
 * the cream triangles on pages 10-24, 60 faces). Same encoding and pairing
 * convention as the crag section above.
 */
export const FOOTHILL_NODE_COUNT = 25;
export const FOOTHILL_TILES = [
    {
        id: "foothill-1",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p10 face @(49,44) / p10 @(201,44)
        faceA: {
            disconnectedPaths: [[8, 9], [11, 15], [12, 16], [20, 22]],
            disconnectedExits: [[1, 1], [3, 1], [10, 1], [14, 3], [24, 3]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(3, "minerals"),
                res(4, "minerals"),
                res(5, "minerals", "oneTime"),
                res(10, "minerals"),
                res(13, "minerals", "oneTime"),
                res(15, "minerals"),
                res(19, "minerals", "oneTime"),
                res(20, "minerals", "oneTime"),
                res(23, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[8, 13], [9, 14], [21, 23]],
            disconnectedExits: [[0, 3], [1, 1], [3, 1], [10, 1], [14, 3], [15, 5]],
            resources: [
                res(2, "minerals", "oneTime"),
                res(3, "minerals"),
                res(5, "minerals"),
                res(13, "minerals", "oneTime"),
                res(16, "minerals"),
                res(21, "minerals", "oneTime"),
                res(22, "minerals"),
                res(23, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-2",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p10 face @(49,307) / p10 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 2], [5, 9], [6, 11], [17, 18], [23, 24]],
            disconnectedExits: [[3, 1], [10, 5], [15, 5], [19, 5]],
            resources: [
                res(0, "vigor"),
                res(2, "minerals", "oneTime"),
                res(6, "minerals"),
                res(8, "minerals"),
                res(10, "minerals"),
                res(12, "minerals", "oneTime"),
                res(16, "vigor", "oneTime"),
                res(20, "minerals", "oneTime"),
                res(22, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [3, 7], [11, 15], [12, 13], [21, 23], [22, 23]],
            disconnectedExits: [[10, 1], [14, 3], [19, 5]],
            resources: [
                res(0, "minerals"),
                res(3, "food", "oneTime"),
                res(5, "minerals", "oneTime"),
                res(11, "minerals"),
                res(12, "minerals"),
                res(14, "minerals"),
                res(17, "minerals", "oneTime"),
                res(20, "minerals", "oneTime"),
                res(21, "food", "oneTime"),
                res(22, "food"),
            ],
        },
    },
    {
        id: "foothill-3",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p11 face @(49,44) / p11 @(201,44)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 4], [11, 15], [16, 19], [17, 18]],
            disconnectedExits: [[10, 5], [14, 3], [15, 5], [24, 5]],
            resources: [
                res(1, "minerals", "oneTime"),
                res(2, "minerals"),
                res(6, "minerals", "oneTime"),
                res(8, "minerals"),
                res(10, "obsidian"),
                res(12, "obsidian", "oneTime"),
                res(14, "obsidian", "oneTime"),
                res(19, "minerals"),
                res(23, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 7], [9, 14], [12, 13], [17, 20], [21, 23]],
            disconnectedExits: [[0, 1], [1, 1], [19, 5], [24, 5]],
            resources: [
                res(2, "minerals", "oneTime"),
                res(4, "minerals"),
                res(5, "food"),
                res(10, "vigor", "oneTime"),
                res(13, "food", "oneTime"),
                res(15, "minerals"),
                res(21, "minerals"),
                res(23, "vigor"),
                res(24, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-4",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p11 face @(49,307) / p11 @(201,307)
        faceA: {
            disconnectedPaths: [[6, 11], [8, 9], [12, 16], [13, 17]],
            disconnectedExits: [[0, 1], [1, 1], [10, 1], [14, 3], [21, 3]],
            resources: [
                res(1, "vigor"),
                res(4, "minerals", "oneTime"),
                res(7, "minerals", "oneTime"),
                res(10, "minerals"),
                res(11, "minerals"),
                res(13, "obsidian", "oneTime"),
                res(14, "obsidian"),
                res(15, "vigor", "oneTime"),
                res(20, "minerals"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 4], [12, 13], [13, 17], [14, 18], [15, 16], [21, 23]],
            disconnectedExits: [[0, 3], [19, 5], [24, 3]],
            resources: [
                res(0, "obsidian"),
                res(3, "minerals", "oneTime"),
                res(4, "food", "oneTime"),
                res(7, "obsidian", "oneTime"),
                res(13, "minerals"),
                res(14, "food"),
                res(15, "minerals"),
                res(17, "food", "oneTime"),
                res(21, "minerals"),
                res(22, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-5",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p12 face @(49,44) / p12 @(201,44)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 4], [17, 18], [20, 22]],
            disconnectedExits: [[6, 1], [14, 3], [22, 5], [24, 3]],
            resources: [
                res(1, "minerals", "oneTime"),
                res(2, "minerals"),
                res(3, "food"),
                res(4, "food", "oneTime"),
                res(11, "minerals"),
                res(12, "minerals"),
                res(17, "food"),
                res(20, "food", "oneTime"),
                res(22, "minerals", "oneTime"),
                res(23, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [1, 4], [6, 11], [9, 14], [12, 16], [19, 20], [23, 24]],
            disconnectedExits: [[15, 5], [21, 3]],
            resources: [
                res(0, "minerals"),
                res(4, "minerals"),
                res(5, "minerals", "oneTime"),
                res(10, "minerals", "oneTime"),
                res(11, "obsidian"),
                res(12, "obsidian", "oneTime"),
                res(18, "minerals"),
                res(23, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-6",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p12 face @(49,307) / p12 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 2], [3, 4], [13, 17], [15, 16], [21, 23], [22, 23]],
            disconnectedExits: [[10, 5], [14, 3], [19, 5]],
            resources: [
                res(2, "vigor", "oneTime"),
                res(3, "vigor"),
                res(5, "vigor", "oneTime"),
                res(6, "minerals"),
                res(11, "minerals", "oneTime"),
                res(13, "vigor"),
                res(17, "minerals"),
                res(19, "vigor", "oneTime"),
                res(24, "vigor"),
            ],
        },
        faceB: {
            disconnectedPaths: [[8, 9], [13, 17], [15, 16], [20, 22]],
            disconnectedExits: [[0, 3], [6, 1], [14, 3], [21, 3], [24, 3]],
            resources: [
                res(2, "food", "oneTime"),
                res(3, "minerals"),
                res(8, "food"),
                res(11, "food", "oneTime"),
                res(13, "food"),
                res(15, "food"),
                res(18, "minerals"),
                res(22, "food", "oneTime"),
                res(23, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-7",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p13 face @(48,44) / p13 @(201,44)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 4], [12, 16], [20, 22], [21, 23]],
            disconnectedExits: [[5, 3], [6, 1], [15, 5], [24, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(2, "minerals"),
                res(10, "minerals", "oneTime"),
                res(11, "minerals"),
                res(12, "obsidian"),
                res(14, "obsidian", "oneTime"),
                res(23, "obsidian"),
                res(24, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 4], [6, 11], [12, 13], [13, 17], [19, 20]],
            disconnectedExits: [[0, 3], [5, 3], [10, 5], [22, 5]],
            resources: [
                res(2, "food"),
                res(3, "minerals"),
                res(7, "vigor", "oneTime"),
                res(11, "minerals", "oneTime"),
                res(13, "minerals"),
                res(17, "food", "oneTime"),
                res(18, "food", "oneTime"),
                res(19, "food"),
                res(23, "vigor"),
            ],
        },
    },
    {
        id: "foothill-8",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p13 face @(48,308) / p13 @(201,307)
        faceA: {
            disconnectedPaths: [[6, 7], [7, 12], [8, 9], [10, 11], [11, 15], [17, 20]],
            disconnectedExits: [[14, 3], [22, 5], [24, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(4, "minerals"),
                res(11, "obsidian"),
                res(14, "food"),
                res(16, "minerals", "oneTime"),
                res(17, "obsidian", "oneTime"),
                res(20, "food", "oneTime"),
                res(23, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [2, 5], [8, 9], [15, 16], [17, 18], [23, 24]],
            disconnectedExits: [[6, 1], [10, 1], [15, 5]],
            resources: [
                res(0, "vigor"),
                res(3, "vigor", "oneTime"),
                res(4, "vigor", "oneTime"),
                res(5, "food"),
                res(10, "minerals"),
                res(12, "minerals"),
                res(13, "food", "oneTime"),
                res(20, "minerals", "oneTime"),
                res(21, "vigor"),
            ],
        },
    },
    {
        id: "foothill-9",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p14 face @(49,44) / p14 @(201,44)
        faceA: {
            disconnectedPaths: [[0, 2], [4, 8], [9, 14], [17, 20], [18, 21]],
            disconnectedExits: [[1, 1], [15, 5], [24, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(3, "vigor", "oneTime"),
                res(4, "vigor"),
                res(9, "minerals"),
                res(11, "minerals", "oneTime"),
                res(16, "minerals"),
                res(17, "obsidian"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[7, 12], [15, 16], [17, 18], [22, 23]],
            disconnectedExits: [[5, 3], [6, 1], [10, 5], [15, 5], [24, 5]],
            resources: [
                res(1, "food"),
                res(3, "minerals", "oneTime"),
                res(9, "food", "oneTime"),
                res(10, "vigor", "oneTime"),
                res(11, "vigor"),
                res(16, "minerals"),
                res(17, "obsidian", "oneTime"),
                res(23, "obsidian"),
            ],
        },
    },
    {
        id: "foothill-10",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p14 face @(49,308) / p14 @(201,307)
        faceA: {
            disconnectedPaths: [[1, 2], [3, 7], [12, 16], [16, 19], [17, 20], [23, 24]],
            disconnectedExits: [[0, 3], [3, 1], [5, 3]],
            resources: [
                res(2, "obsidian"),
                res(9, "vigor", "oneTime"),
                res(11, "obsidian", "oneTime"),
                res(16, "vigor"),
                res(17, "vigor"),
                res(20, "minerals", "oneTime"),
                res(22, "minerals"),
                res(24, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [16, 19], [17, 20], [18, 21], [23, 24]],
            disconnectedExits: [[0, 1], [3, 1], [10, 1], [22, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(1, "food", "oneTime"),
                res(6, "obsidian", "oneTime"),
                res(7, "minerals"),
                res(8, "food"),
                res(14, "obsidian", "oneTime"),
                res(21, "obsidian"),
                res(22, "food"),
            ],
        },
    },
    {
        id: "foothill-11",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p15 face @(49,44) / p15 @(201,44)
        faceA: {
            disconnectedPaths: [[7, 12], [8, 9], [9, 14], [11, 15], [17, 20]],
            disconnectedExits: [[3, 1], [19, 5], [21, 3], [24, 5]],
            resources: [
                res(1, "food"),
                res(9, "food"),
                res(10, "food", "oneTime"),
                res(11, "minerals"),
                res(12, "food"),
                res(17, "food", "oneTime"),
                res(18, "food", "oneTime"),
                res(24, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 4], [7, 12], [12, 13], [22, 23]],
            disconnectedExits: [[0, 1], [10, 1], [14, 3], [15, 5], [21, 3]],
            resources: [
                res(2, "vigor", "oneTime"),
                res(5, "vigor", "oneTime"),
                res(6, "vigor", "oneTime"),
                res(8, "vigor"),
                res(10, "vigor", "oneTime"),
                res(12, "minerals"),
                res(16, "vigor"),
                res(23, "vigor"),
            ],
        },
    },
    {
        id: "foothill-12",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p15 face @(49,308) / p15 @(201,307)
        faceA: {
            disconnectedPaths: [[7, 12], [8, 13], [14, 18]],
            disconnectedExits: [[1, 1], [10, 1], [19, 5], [21, 3], [22, 5], [24, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(6, "food"),
                res(9, "food", "oneTime"),
                res(14, "food"),
                res(15, "food", "oneTime"),
                res(16, "food"),
                res(21, "food"),
                res(23, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 4], [8, 9], [10, 11], [12, 13], [18, 21], [20, 22], [21, 23]],
            disconnectedExits: [[1, 1], [24, 5]],
            resources: [
                res(2, "obsidian", "oneTime"),
                res(4, "minerals"),
                res(9, "obsidian", "oneTime"),
                res(10, "obsidian", "oneTime"),
                res(12, "minerals", "oneTime"),
                res(13, "obsidian"),
                res(21, "obsidian"),
                res(22, "obsidian"),
            ],
        },
    },
    {
        id: "foothill-13",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p16 face @(49,44) / p16 @(201,44)
        faceA: {
            disconnectedPaths: [[1, 2], [3, 7], [5, 9], [12, 16], [13, 17]],
            disconnectedExits: [[0, 3], [10, 1], [15, 5], [21, 3]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(2, "vigor"),
                res(3, "vigor", "oneTime"),
                res(4, "minerals", "oneTime"),
                res(5, "vigor"),
                res(11, "vigor", "oneTime"),
                res(16, "vigor"),
                res(17, "vigor"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[3, 4], [10, 11], [17, 18], [19, 20], [20, 22]],
            disconnectedExits: [[0, 3], [15, 5], [21, 3], [24, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(4, "food"),
                res(7, "food"),
                res(10, "food", "oneTime"),
                res(13, "food", "oneTime"),
                res(14, "food", "oneTime"),
                res(20, "obsidian"),
                res(23, "food"),
            ],
        },
    },
    {
        id: "foothill-14",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p16 face @(49,308) / p16 @(201,307)
        faceA: {
            disconnectedPaths: [[2, 5], [3, 4], [4, 8], [16, 19], [17, 18], [23, 24]],
            disconnectedExits: [[0, 3], [10, 1], [24, 3]],
            resources: [
                res(0, "obsidian"),
                res(2, "food", "oneTime"),
                res(3, "obsidian"),
                res(11, "obsidian", "oneTime"),
                res(16, "food"),
                res(21, "obsidian", "oneTime"),
                res(22, "food", "oneTime"),
                res(23, "food"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [7, 12], [8, 9], [15, 16], [20, 22], [21, 23]],
            disconnectedExits: [[1, 1], [6, 1], [22, 5]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(7, "vigor"),
                res(9, "vigor"),
                res(10, "vigor", "oneTime"),
                res(14, "vigor", "oneTime"),
                res(18, "vigor", "oneTime"),
                res(19, "vigor"),
                res(23, "obsidian"),
            ],
        },
    },
    {
        id: "foothill-15",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p17 face @(48,45) / p17 @(201,46)
        faceA: {
            disconnectedPaths: [[0, 2], [3, 7], [4, 8], [5, 9], [17, 20], [22, 23]],
            disconnectedExits: [[3, 1], [15, 5], [24, 5]],
            resources: [
                res(0, "obsidian"),
                res(2, "vigor", "oneTime"),
                res(3, "obsidian"),
                res(16, "vigor"),
                res(17, "obsidian", "oneTime"),
                res(20, "vigor", "oneTime"),
                res(21, "vigor"),
                res(23, "obsidian", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [7, 12], [8, 13], [15, 16], [18, 21]],
            disconnectedExits: [[10, 5], [14, 3], [22, 5], [24, 3]],
            resources: [
                res(0, "minerals"),
                res(4, "minerals"),
                res(6, "obsidian", "oneTime"),
                res(8, "minerals"),
                res(15, "minerals"),
                res(16, "food", "oneTime"),
                res(21, "obsidian", "oneTime"),
                res(24, "vigor", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-16",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p17 face @(48,309) / p17 @(201,309)
        faceA: {
            disconnectedPaths: [[8, 13], [15, 16], [17, 20]],
            disconnectedExits: [[1, 1], [3, 1], [10, 5], [15, 5], [19, 5], [24, 3]],
            resources: [
                res(0, "food", "oneTime"),
                res(6, "food", "oneTime"),
                res(8, "minerals"),
                res(11, "minerals"),
                res(13, "food", "oneTime"),
                res(14, "food", "oneTime"),
                res(17, "food"),
                res(20, "minerals"),
                res(22, "minerals", "oneTime"),
                res(23, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[7, 12], [8, 9], [17, 20], [18, 21]],
            disconnectedExits: [[0, 1], [10, 5], [14, 3], [15, 5], [24, 5]],
            resources: [
                res(0, "minerals"),
                res(1, "food", "oneTime"),
                res(9, "vigor", "oneTime"),
                res(11, "food", "oneTime"),
                res(13, "minerals"),
                res(17, "vigor", "oneTime"),
                res(20, "minerals"),
                res(21, "minerals"),
                res(24, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-17",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p18 face @(49,45) / p18 @(201,46)
        faceA: {
            disconnectedPaths: [[1, 2], [7, 12], [9, 14], [12, 16], [19, 20]],
            disconnectedExits: [[3, 1], [5, 3], [10, 5], [19, 5]],
            resources: [
                res(0, "minerals"),
                res(2, "minerals"),
                res(7, "vigor", "oneTime"),
                res(10, "vigor", "oneTime"),
                res(13, "minerals"),
                res(14, "vigor", "oneTime"),
                res(16, "minerals"),
                res(21, "vigor", "oneTime"),
                res(23, "minerals", "oneTime"),
                res(24, "vigor"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [1, 4], [5, 9], [7, 12], [14, 18], [15, 16], [21, 23]],
            disconnectedExits: [[22, 5], [24, 3]],
            resources: [
                res(2, "obsidian"),
                res(4, "minerals", "oneTime"),
                res(5, "obsidian", "oneTime"),
                res(11, "minerals"),
                res(15, "obsidian", "oneTime"),
                res(16, "minerals"),
                res(18, "minerals"),
                res(23, "minerals"),
            ],
        },
    },
    {
        id: "foothill-18",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p18 face @(49,309) / p18 @(201,309)
        faceA: {
            disconnectedPaths: [[6, 7], [8, 9], [13, 17], [15, 16], [21, 23]],
            disconnectedExits: [[0, 3], [10, 5], [22, 5], [24, 5]],
            resources: [
                res(1, "minerals"),
                res(6, "obsidian", "oneTime"),
                res(11, "food", "oneTime"),
                res(13, "obsidian"),
                res(14, "minerals"),
                res(15, "food"),
                res(21, "obsidian", "oneTime"),
                res(22, "minerals", "oneTime"),
                res(24, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[4, 8], [11, 15], [17, 18], [17, 20], [21, 23]],
            disconnectedExits: [[0, 1], [6, 1], [14, 3], [19, 5]],
            resources: [
                res(1, "minerals", "oneTime"),
                res(2, "food", "oneTime"),
                res(4, "minerals"),
                res(7, "minerals"),
                res(8, "minerals"),
                res(10, "vigor", "oneTime"),
                res(14, "food"),
                res(22, "vigor"),
                res(24, "vigor", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-19",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p19 face @(49,45) / p19 @(201,46)
        faceA: {
            disconnectedPaths: [[1, 2], [3, 7], [8, 9], [14, 18], [16, 19]],
            disconnectedExits: [[0, 3], [6, 1], [22, 5], [24, 5]],
            resources: [
                res(0, "obsidian"),
                res(3, "vigor", "oneTime"),
                res(4, "minerals", "oneTime"),
                res(7, "vigor", "oneTime"),
                res(9, "minerals"),
                res(10, "obsidian", "oneTime"),
                res(16, "minerals"),
                res(17, "vigor"),
                res(23, "minerals"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [9, 14], [11, 15], [13, 17], [20, 22]],
            disconnectedExits: [[1, 1], [10, 1], [21, 3], [24, 3]],
            resources: [
                res(2, "food"),
                res(6, "food", "oneTime"),
                res(7, "minerals"),
                res(10, "food"),
                res(11, "food", "oneTime"),
                res(13, "food", "oneTime"),
                res(17, "food", "oneTime"),
                res(18, "minerals"),
                res(20, "minerals"),
                res(23, "food", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-20",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p19 face @(49,309) / p19 @(201,309)
        faceA: {
            disconnectedPaths: [[3, 7], [5, 9], [7, 12], [9, 14], [11, 15], [13, 17], [16, 19]],
            disconnectedExits: [[22, 5], [24, 5]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(7, "minerals"),
                res(9, "obsidian"),
                res(10, "obsidian", "oneTime"),
                res(11, "obsidian", "oneTime"),
                res(18, "minerals", "oneTime"),
                res(22, "minerals"),
                res(24, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [8, 9], [11, 15], [16, 19]],
            disconnectedExits: [[6, 1], [19, 5], [21, 3], [24, 3]],
            resources: [
                res(0, "minerals"),
                res(3, "vigor", "oneTime"),
                res(6, "vigor", "oneTime"),
                res(7, "minerals"),
                res(8, "vigor", "oneTime"),
                res(10, "vigor"),
                res(16, "vigor"),
                res(18, "minerals"),
                res(22, "vigor", "oneTime"),
                res(23, "vigor", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-21",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p20 face @(49,45) / p20 @(201,46)
        faceA: {
            disconnectedPaths: [[4, 8], [11, 15], [17, 20]],
            disconnectedExits: [[1, 1], [10, 1], [10, 5], [14, 3], [22, 5]],
            resources: [
                res(0, "food"),
                res(4, "food"),
                res(6, "food", "oneTime"),
                res(11, "food", "oneTime"),
                res(14, "food"),
                res(16, "minerals"),
                res(17, "minerals"),
                res(20, "minerals", "oneTime"),
                res(24, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[8, 9], [11, 15], [12, 16], [14, 18]],
            disconnectedExits: [[0, 1], [1, 1], [14, 3], [19, 5], [24, 5]],
            resources: [
                res(0, "vigor", "oneTime"),
                res(2, "vigor"),
                res(6, "vigor", "oneTime"),
                res(7, "minerals", "oneTime"),
                res(12, "vigor"),
                res(14, "vigor", "oneTime"),
                res(15, "minerals"),
                res(20, "vigor"),
                res(21, "minerals"),
                res(23, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-22",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p20 face @(49,309) / p20 @(201,309)
        faceA: {
            disconnectedPaths: [[12, 13], [17, 18], [21, 23]],
            disconnectedExits: [[1, 1], [3, 1], [5, 3], [10, 5], [15, 5], [24, 5]],
            resources: [
                res(1, "vigor", "oneTime"),
                res(2, "vigor"),
                res(3, "vigor", "oneTime"),
                res(6, "food", "oneTime"),
                res(13, "minerals"),
                res(15, "food"),
                res(17, "food", "oneTime"),
                res(18, "food"),
                res(20, "food", "oneTime"),
                res(23, "minerals"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [4, 8], [7, 12], [17, 20], [18, 21], [21, 23]],
            disconnectedExits: [[1, 1], [14, 3], [24, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(2, "minerals", "oneTime"),
                res(3, "minerals", "oneTime"),
                res(4, "minerals"),
                res(11, "obsidian", "oneTime"),
                res(15, "obsidian", "oneTime"),
                res(18, "obsidian"),
                res(19, "minerals"),
                res(24, "obsidian"),
            ],
        },
    },
    {
        id: "foothill-23",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p21 face @(48,45) / p21 @(201,46)
        faceA: {
            disconnectedPaths: [[1, 4], [8, 13], [15, 16]],
            disconnectedExits: [[0, 3], [1, 1], [3, 1], [21, 3], [22, 5], [24, 5]],
            resources: [
                res(0, "vigor", "oneTime"),
                res(4, "vigor"),
                res(5, "minerals"),
                res(9, "vigor", "oneTime"),
                res(12, "food"),
                res(15, "minerals"),
                res(19, "vigor", "oneTime"),
                res(20, "food", "oneTime"),
                res(22, "vigor"),
                res(24, "food", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [7, 12], [17, 20], [21, 23]],
            disconnectedExits: [[1, 1], [5, 3], [10, 1], [19, 5], [24, 3]],
            resources: [
                res(4, "minerals"),
                res(5, "minerals", "oneTime"),
                res(6, "vigor", "oneTime"),
                res(7, "vigor", "oneTime"),
                res(10, "vigor"),
                res(14, "minerals", "oneTime"),
                res(16, "vigor"),
                res(18, "minerals"),
                res(22, "minerals"),
                res(24, "minerals", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-24",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p21 face @(48,309) / p21 @(201,309)
        faceA: {
            disconnectedPaths: [[1, 2], [2, 5], [3, 4], [6, 11], [12, 13], [17, 18]],
            disconnectedExits: [[0, 3], [19, 5], [24, 5]],
            resources: [
                res(2, "obsidian"),
                res(6, "vigor", "oneTime"),
                res(8, "minerals"),
                res(11, "obsidian", "oneTime"),
                res(16, "obsidian", "oneTime"),
                res(17, "minerals"),
                res(22, "minerals", "oneTime"),
                res(24, "vigor"),
            ],
        },
        faceB: {
            disconnectedPaths: [[7, 12], [8, 9], [10, 11], [19, 20], [22, 23]],
            disconnectedExits: [[0, 3], [10, 1], [14, 3], [24, 5]],
            resources: [
                res(4, "minerals"),
                res(5, "food", "oneTime"),
                res(7, "obsidian", "oneTime"),
                res(9, "food"),
                res(10, "obsidian"),
                res(12, "minerals", "oneTime"),
                res(21, "obsidian", "oneTime"),
                res(22, "minerals"),
            ],
        },
    },
    {
        id: "foothill-25",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p22 face @(49,45) / p22 @(201,46)
        faceA: {
            disconnectedPaths: [[1, 2], [7, 12], [8, 9], [14, 18], [17, 18]],
            disconnectedExits: [[0, 3], [5, 3], [10, 5], [15, 5]],
            resources: [
                res(0, "vigor", "oneTime"),
                res(4, "vigor", "oneTime"),
                res(5, "obsidian"),
                res(6, "food", "oneTime"),
                res(8, "food"),
                res(14, "food", "oneTime"),
                res(15, "minerals"),
                res(20, "vigor"),
                res(22, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [3, 7], [10, 11], [11, 15], [12, 13]],
            disconnectedExits: [[1, 1], [5, 3], [15, 5], [21, 3]],
            resources: [
                res(2, "food"),
                res(3, "obsidian", "oneTime"),
                res(4, "food"),
                res(11, "obsidian"),
                res(13, "food", "oneTime"),
                res(17, "food", "oneTime"),
                res(23, "minerals"),
                res(24, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-26",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p22 face @(49,309) / p22 @(201,309)
        faceA: {
            disconnectedPaths: [[8, 13], [11, 15], [19, 20], [22, 23]],
            disconnectedExits: [[1, 1], [5, 3], [10, 5], [14, 3], [19, 5]],
            resources: [
                res(0, "food", "oneTime"),
                res(2, "food", "oneTime"),
                res(6, "food"),
                res(7, "minerals", "oneTime"),
                res(9, "food"),
                res(13, "food"),
                res(21, "minerals", "oneTime"),
                res(22, "minerals"),
                res(24, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[2, 5], [8, 9], [11, 15], [14, 18], [20, 22]],
            disconnectedExits: [[0, 3], [1, 1], [6, 1]],
            resources: [
                res(4, "vigor", "oneTime"),
                res(5, "vigor"),
                res(7, "minerals"),
                res(10, "obsidian", "oneTime"),
                res(13, "vigor", "oneTime"),
                res(17, "vigor"),
                res(22, "obsidian"),
                res(24, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-27",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p23 face @(49,45) / p23 @(201,46)
        faceA: {
            disconnectedPaths: [[7, 12], [8, 9], [22, 23]],
            disconnectedExits: [[0, 3], [1, 1], [5, 3], [10, 5], [14, 3], [24, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(5, "vigor"),
                res(7, "vigor"),
                res(14, "vigor"),
                res(16, "minerals"),
                res(17, "vigor", "oneTime"),
                res(20, "minerals", "oneTime"),
                res(22, "minerals", "oneTime"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[0, 2], [5, 9], [6, 7], [6, 11], [8, 9], [16, 19], [17, 20]],
            disconnectedExits: [[22, 5], [24, 3]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(4, "obsidian", "oneTime"),
                res(5, "obsidian"),
                res(6, "obsidian"),
                res(17, "minerals"),
                res(19, "obsidian"),
                res(21, "obsidian", "oneTime"),
                res(24, "obsidian", "oneTime"),
            ],
        },
    },
    {
        id: "foothill-28",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p23 face @(49,309) / p23 @(201,309)
        faceA: {
            disconnectedPaths: [[2, 5], [4, 8], [16, 19], [17, 18], [21, 23], [22, 23]],
            disconnectedExits: [[6, 1], [15, 5], [24, 5]],
            resources: [
                res(2, "vigor"),
                res(3, "minerals", "oneTime"),
                res(7, "minerals", "oneTime"),
                res(13, "vigor", "oneTime"),
                res(15, "vigor"),
                res(17, "vigor"),
                res(21, "vigor"),
                res(22, "minerals", "oneTime"),
                res(23, "minerals", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [6, 11], [8, 9], [11, 15], [18, 21]],
            disconnectedExits: [[0, 3], [3, 1], [19, 5], [22, 5]],
            resources: [
                res(0, "minerals", "oneTime"),
                res(2, "minerals", "oneTime"),
                res(6, "food"),
                res(8, "food"),
                res(11, "minerals", "oneTime"),
                res(13, "minerals", "oneTime"),
                res(15, "food"),
                res(21, "food", "oneTime"),
                res(23, "food"),
            ],
        },
    },
    {
        id: "foothill-29",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p24 face @(49,45) / p24 @(201,46)
        faceA: {
            disconnectedPaths: [[3, 4], [5, 9], [7, 12], [11, 15], [13, 17], [16, 19]],
            disconnectedExits: [[0, 3], [5, 3], [22, 5], [24, 5]],
            resources: [
                res(0, "obsidian", "oneTime"),
                res(1, "food", "oneTime"),
                res(11, "obsidian"),
                res(12, "food", "oneTime"),
                res(13, "food"),
                res(14, "obsidian", "oneTime"),
                res(20, "food"),
                res(23, "food"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [2, 5], [3, 4], [5, 9], [11, 15], [12, 13]],
            disconnectedExits: [[6, 1], [10, 5], [24, 5]],
            resources: [
                res(2, "obsidian"),
                res(5, "vigor", "oneTime"),
                res(7, "vigor"),
                res(10, "obsidian", "oneTime"),
                res(13, "vigor"),
                res(14, "vigor", "oneTime"),
                res(19, "obsidian", "oneTime"),
                res(23, "vigor"),
            ],
        },
    },
    {
        id: "foothill-30",
        zone: "foothill",
        nodeCount: FOOTHILL_NODE_COUNT,
        // PDF p24 face @(49,309) / p24 @(201,309)
        faceA: {
            disconnectedPaths: [[3, 7], [7, 12], [8, 13], [10, 11], [15, 16], [22, 23]],
            disconnectedExits: [[5, 3], [14, 3], [24, 3]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(3, "obsidian"),
                res(5, "obsidian", "oneTime"),
                res(9, "vigor"),
                res(15, "obsidian"),
                res(17, "obsidian", "oneTime"),
                res(22, "vigor"),
                res(24, "vigor", "oneTime"),
            ],
        },
        faceB: {
            disconnectedPaths: [[1, 2], [8, 9], [11, 15], [12, 16], [18, 21], [19, 20], [21, 23]],
            disconnectedExits: [[3, 1], [24, 5]],
            resources: [
                res(1, "obsidian", "oneTime"),
                res(2, "obsidian", "oneTime"),
                res(7, "food"),
                res(9, "food"),
                res(11, "food", "oneTime"),
                res(18, "obsidian", "oneTime"),
                res(19, "obsidian"),
                res(24, "obsidian"),
            ],
        },
    },
];
// --- Forest -----------------------------------------------------------------
//
// NOT CATALOGUED YET — the physical forest tiles don't fit the current board.
//
// The PDF (pages 25-30, one tile per page shown as 2 faces × 2 half-strips)
// and the rulebook agree: the 6 forest "perimeter tiles" are 16-hex-long,
// one-hex-row-tall strips of ~64 nodes that enclose the board as a whole
// outer hex ring — i.e. the physical board is BOARD_RADIUS 16, not 15, and
// forest = triangle rows 16-17 (6 × 64 = 384 nodes, matching the rulebook
// node count in `config/vigor_zones.ts`). The current tessellation
// (`tiles/slots.ts`) has no outer ring: its forest slots are 31-node slivers
// of row 16 only, so a 64-node tile can't be authored against them.
// The strips also carry a marker the tile schema can't hold yet: circled
// nodes = valid STARTING-MINE positions (rulebook "circled dot on the board
// perimeter").
//
// The extracted per-face data (resources, no severed paths, start-position
// nodes) is parked in `./forest_tiles.extracted.json` until the board
// gains its outer ring.
//# sourceMappingURL=tiles.js.map